import { BrowserRouter, Navigate, Route, Routes, Link } from 'react-router-dom';
import { AppLayout } from '@/layout/AppLayout';
import { Button } from '@/components/ui/button';
import { RoutePlaceholder } from '@/pages/RoutePlaceholder';
import type { RouteDefinition } from '@/routes';
import { routes } from '@/routes';
import { FireCalculatorPage } from '@/pages/calculators/FireCalculatorPage';
import { AdvancedFireCalculatorPage } from '@/pages/calculators/AdvancedFireCalculatorPage';
import { CoastFireCalculatorPage } from '@/pages/calculators/CoastFireCalculatorPage';
import { CompoundInterestCalculatorPage } from '@/pages/calculators/CompoundInterestCalculatorPage';
import { RetirementGoalCalculatorPage } from '@/pages/calculators/RetirementGoalCalculatorPage';
import { BudgetPlannerCalculatorPage } from '@/pages/calculators/BudgetPlannerCalculatorPage';
import { DebtPayoffCalculatorPage } from '@/pages/calculators/DebtPayoffCalculatorPage';
import { EmergencyFundCalculatorPage } from '@/pages/calculators/EmergencyFundCalculatorPage';
import { MortgageCalculatorPage } from '@/pages/calculators/MortgageCalculatorPage';
import { PaycheckCalculatorPage } from '@/pages/calculators/PaycheckCalculatorPage';
import { RentVsBuyCalculatorPage } from '@/pages/calculators/RentVsBuyCalculatorPage';
import { JobOfferCalculatorPage } from '@/pages/calculators/JobOfferCalculatorPage';
import { EquityCalculatorPage } from '@/pages/calculators/EquityCalculatorPage';
import { ContentPage } from '@/pages/content/ContentPage';
import { ChangelogPage } from '@/pages/content/ChangelogPage';
import { LegacyGuidePage } from '@/pages/content/LegacyGuidePage';
import { GlossaryPage } from '@/pages/content/GlossaryPage';
import { LegalPage } from '@/pages/content/LegalPage';
import { GuidesHubPage } from '@/pages/content/GuidesHubPage';

const legacyGuidePaths = new Set([
  '/guides/first-time-homebuyer',
  '/guides/investing-basics',
  '/guides/debt-payoff-strategies'
]);

function NotFound() {
  return (
    <AppLayout>
      <section className="brand-frame rounded-xl border border-border bg-card/85 p-8 text-center">
        <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">404</p>
        <h1 className="mt-3 font-display text-4xl text-foreground">Route Not Found</h1>
        <p className="mx-auto mt-2 max-w-xl text-sm text-muted-foreground">
          The page you requested was not found. Jump back to the main calculator hub or review the site FAQ.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button asChild>
            <Link to="/">Open FIRE Calculator</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/faq/">Open FAQ</Link>
          </Button>
        </div>
      </section>
    </AppLayout>
  );
}

export function App() {
  return (
    <BrowserRouter>
      <Routes>
        {routes.map((route) => (
          <Route key={route.key} path={route.path} element={renderRoute(route)} />
        ))}
        <Route path="/index" element={<Navigate to="/" replace />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </BrowserRouter>
  );
}

function renderRoute(route: RouteDefinition) {
  if (route.category === 'content') {
    if (route.canonicalPath === '/changelog') {
      return <ChangelogPage route={route} />;
    }

    if (route.canonicalPath === '/glossary') {
      return <GlossaryPage route={route} />;
    }

    if (route.canonicalPath === '/guides') {
      return <GuidesHubPage route={route} />;
    }

    if (legacyGuidePaths.has(route.canonicalPath)) {
      return <LegacyGuidePage route={route} />;
    }

    if (route.canonicalPath === '/privacy-policy' || route.canonicalPath === '/terms-of-service') {
      return <LegalPage route={route} />;
    }

    return <ContentPage route={route} />;
  }

  switch (route.canonicalPath) {
    case '/':
      return <FireCalculatorPage route={route} />;
    case '/advanced-calculator':
      return <AdvancedFireCalculatorPage route={route} />;
    case '/coast-fire-calculator':
      return <CoastFireCalculatorPage route={route} />;
    case '/compound-interest':
      return <CompoundInterestCalculatorPage route={route} />;
    case '/retirement-goal-calculator':
      return <RetirementGoalCalculatorPage route={route} />;
    case '/budget-planner':
      return <BudgetPlannerCalculatorPage route={route} />;
    case '/debt-payoff':
      return <DebtPayoffCalculatorPage route={route} />;
    case '/emergency-fund':
      return <EmergencyFundCalculatorPage route={route} />;
    case '/mortgage-calculator':
      return <MortgageCalculatorPage route={route} />;
    case '/paycheck-calculator':
      return <PaycheckCalculatorPage route={route} />;
    case '/rent-vs-buy':
      return <RentVsBuyCalculatorPage route={route} />;
    case '/job-offer-comparison':
      return <JobOfferCalculatorPage route={route} />;
    case '/equity-calculator':
      return <EquityCalculatorPage route={route} />;
    default:
      return <RoutePlaceholder route={route} />;
  }
}
